import {buildAssetEmbedSrc, knowledgeObjectUidFromEmbedSrc} from "./asset-embed.helper";
import type {WhiteboardImageLookup} from "./interfaces/whiteboard-image-lookup.interface";

/**
 * Prefix of the text `@o2d/whiteboard` puts on the clipboard for its objects, followed by a JSON
 * array of board objects. Kept here because the package does not export it
 * (`packages/whiteboard/src/core/state/clipboard-payload.ts`, `CLIPBOARD_MARKER`). Should the
 * package change the prefix, pastes simply stop matching and upload as before.
 */
const BOARD_MARKER = 'o2d-whiteboard/v1:';

/** A ShortId: 22 characters of the Base64URL alphabet. */
const SHORT_ID = /^[A-Za-z0-9_-]{22}$/;

/** The src an older board stored for an image: the plain asset route. */
const ASSET_SRC = /^\/api\/v1\/files\/([A-Za-z0-9_-]{22})$/;

/**
 * Finds the image object a picture pasted from a whiteboard already belongs to, so a document
 * embeds that object instead of uploading a second copy.
 *
 * The browser re-encodes a picture on its way through the system clipboard, so the pasted bytes
 * never match the stored ones and the upload's checksum dedup cannot help. The board's own text
 * on the clipboard still names the object. Only a clipboard holding exactly one board image that
 * addresses an image object of this tenant is resolved; anything else returns null and the editor
 * uploads the file as before. The returned src is always rebuilt here, never taken from the
 * clipboard text, because the editor inserts nothing at all for an unsafe src.
 *
 * @param text       The pasted `text/plain`.
 * @param repository The lookups (`KnowledgeObjectRepository`).
 * @returns          `{src}` of the existing image object, or null.
 */
export async function resolveWhiteboardImagePaste(text: string, repository: WhiteboardImageLookup): Promise<{ src: string } | null> {
    try {
        const src = singleBoardImageSrc(text);
        if (src === null) {
            return null;
        }

        const objectUid = await imageObjectUidFor(src, repository);

        return objectUid === null ? null : {src: buildAssetEmbedSrc(objectUid)};
    } catch {
        return null;
    }
}

/** The src of the one image a board marker carries, or null for any other clipboard text. */
function singleBoardImageSrc(text: string): string | null {
    if (!text.startsWith(BOARD_MARKER)) {
        return null;
    }
    const objects: unknown = JSON.parse(text.slice(BOARD_MARKER.length));
    if (!Array.isArray(objects) || objects.length !== 1) {
        return null;
    }
    const object = objects[0] as { type?: unknown; src?: unknown } | null;

    return object?.type === 'image' && typeof object.src === 'string' ? object.src : null;
}

/** The uid of the live image object a board image src addresses, or null. */
async function imageObjectUidFor(src: string, repository: WhiteboardImageLookup): Promise<string | null> {
    const objectUid = knowledgeObjectUidFromEmbedSrc(src);
    if (objectUid !== null) {
        if (!SHORT_ID.test(objectUid)) {
            return null;
        }
        const object = await repository.findOneById(objectUid);

        return object !== null && object.type === 'image' && !object.isDeleted ? objectUid : null;
    }

    const assetUid = ASSET_SRC.exec(src)?.[1];

    return assetUid === undefined ? null : repository.findImageObjectUidByAsset(assetUid);
}
